// E8CatDiscountExpressionClosedTask.js
// Zadanie zamkniete E8 (2026 zad. 5) – zapis wyrazenia algebraicznego z procentami:
// kocur za x zl ze znizka d1%, kotka za y zl ze znizka d2% => placi
// (1-d1/100)x + (1-d2/100)y.

import React from "react";
import { ChoiceOptionMath, InlineMath } from "../../../../components/math/KatexWrappers";
import ABCDInput from "../../../../components/inputs/ABCDInput";

const LETTERS = ["A", "B", "C", "D"];

// znizki dajace "ladne" wspolczynniki (bez 50%, by uniknac kolizji znizka=zaplata)
const DISCOUNTS = [10, 20, 25, 40, 60, 75, 80];
const PAID = { 10: "0{,}9", 20: "0{,}8", 25: "0{,}75", 40: "0{,}6", 60: "0{,}4", 75: "0{,}25", 80: "0{,}2" };
const DISC = { 10: "0{,}1", 20: "0{,}2", 25: "0{,}25", 40: "0{,}4", 60: "0{,}6", 75: "0{,}75", 80: "0{,}8" };

function pick(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

function shuffle(arr) {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function expr(fx, fy) {
  return `${fx}x+${fy}y`;
}

function buildOptions(d1, d2) {
  const correct = expr(PAID[d1], PAID[d2]);
  const distractors = [
    expr(DISC[d1], DISC[d2]), // znizki potraktowane jako zaplata
    expr(DISC[d2], DISC[d1]), // zamienione znizki
    expr(PAID[d2], PAID[d1]), // zamienione wspolczynniki zaplaty
  ];
  return { correct, all: [correct, ...distractors] };
}

function buildData({ d1, d2, fixedOptions = null }) {
  const { correct } = buildOptions(d1, d2);

  let options;
  let correctIndex;
  if (fixedOptions) {
    options = fixedOptions.map((latex, index) => ({ latex, label: LETTERS[index] }));
    correctIndex = fixedOptions.indexOf(correct);
  } else {
    const ordered = shuffle(buildOptions(d1, d2).all);
    options = ordered.map((latex, index) => ({ latex, label: LETTERS[index] }));
    correctIndex = ordered.indexOf(correct);
  }

  return { d1, d2, correct, options, correctIndex };
}

function createOriginalTask() {
  return {
    type: "E8CatDiscountExpressionClosedTask",
    data: buildData({
      d1: 40,
      d2: 20,
      fixedOptions: [
        "0{,}4x+0{,}2y",
        "0{,}2x+0{,}4y",
        "0{,}8x+0{,}6y",
        "0{,}6x+0{,}8y",
      ],
    }),
    source: {
      variant: "original",
      exam: "E8",
      session: "2026_MAJ",
      taskNumber: 5,
    },
    tags: ["e8", "algebra", "wyrazenia-algebraiczne", "procenty"],
  };
}

export function generateTask(generationOptions = {}) {
  if (generationOptions.variant === "original") return createOriginalTask();

  let d1;
  let d2;
  let all;
  do {
    d1 = pick(DISCOUNTS);
    d2 = pick(DISCOUNTS);
    all = buildOptions(d1, d2).all;
  } while (d1 === d2 || new Set(all).size !== 4);

  return {
    type: "E8CatDiscountExpressionClosedTask",
    data: buildData({ d1, d2 }),
    source: { variant: "random", basedOn: "E8_2026_MAJ_5" },
    tags: ["e8", "algebra", "wyrazenia-algebraiczne", "procenty"],
  };
}

export function renderTask({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p>
        W pewnej hodowli rasowy kocur kosztuje <InlineMath math="x" /> złotych, a
        rasowa kotka kosztuje <InlineMath math="y" /> złotych. Janek kupił z tej
        hodowli rasowego kocura ze zniżką <InlineMath math={`${data.d1}\\%`} /> oraz
        rasową kotkę ze zniżką <InlineMath math={`${data.d2}\\%`} />.
      </p>
      <p>
        Które wyrażenie poprawnie opisuje, ile złotych zapłacił Janek za te koty?
        Wybierz właściwą odpowiedź spośród podanych.
      </p>
      <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 8, marginTop: 10 }}>
        {data.options.map((option, index) => (
          <div key={option.latex}>
            <ChoiceOptionMath letter={LETTERS[index]} math={option.latex} />
          </div>
        ))}
      </div>
    </div>
  );
}

export function renderInput({ data }, userAnswer, onChange, disabled) {
  return (
    <ABCDInput
      options={data.options.map((option, index) => ({
        label: LETTERS[index],
        textNode: LETTERS[index],
      }))}
      value={userAnswer}
      onChange={onChange}
      disabled={disabled}
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  return userAnswer === data.correctIndex;
}

export function renderAnswer({ data }) {
  return (
    <span>
      <ChoiceOptionMath letter={LETTERS[data.correctIndex]} math={data.correct} />
    </span>
  );
}

export function renderUserAnswer(userAnswer, { data }) {
  if (typeof userAnswer !== "number") return <i>(brak odpowiedzi)</i>;
  const option = data.options[userAnswer];
  if (!option) return <i>(nieprawidłowy wybór)</i>;
  return (
    <span>
      <ChoiceOptionMath letter={LETTERS[userAnswer]} math={option.latex} />
    </span>
  );
}

export function renderExplanation({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.75 }}>
      <p>
        <strong>Krok 1.</strong> Zniżka <InlineMath math={`${data.d1}\\%`} /> oznacza,
        że za kocura Janek płaci <InlineMath math={`100\\%-${data.d1}\\%=${100 - data.d1}\\%`} />{" "}
        ceny, czyli <InlineMath math={`${PAID[data.d1]}x`} />.
      </p>
      <p>
        <strong>Krok 2.</strong> Podobnie za kotkę płaci{" "}
        <InlineMath math={`100\\%-${data.d2}\\%=${100 - data.d2}\\%`} /> ceny, czyli{" "}
        <InlineMath math={`${PAID[data.d2]}y`} />.
      </p>
      <p>
        <strong>Krok 3.</strong> Łącznie Janek zapłacił{" "}
        <InlineMath math={data.correct} />.
      </p>
      <p>
        Poprawna odpowiedź to{" "}
        <InlineMath math={`\\text{${LETTERS[data.correctIndex]}}`} />.
      </p>
    </div>
  );
}
